<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import type { Employee } from '@/types/employee';
import type { Module } from '@/types/module';
import type { NameIdPair } from '@/types/nameIdPair';
import type { Organization } from '@/types/organization';
import type { Risk } from '@/types/risk';
import type { Team } from '@/types/team';

import toast from '@/utils/toast';

import DotText from '@/components/DotText.vue';
import { useAuthStore } from '@/stores/auth';
import { usePriorityStore } from '@/stores/priority';
import { actionPointStatusTypeItems, type ActionPointStatus, type DetailedActionPoint } from '@/types/actionPoint';
import type { Priority } from '@/types/priority';

import Chip from '@/components/Chip.vue';
import CustomButton from '@/components/CustomButton.vue';
import ActionPointDetailsDialog from '@/components/dialogs/ActionPointDetailsDialog.vue';
import CreateActionPointDialog from '@/components/dialogs/CreateActionPointDialog.vue';
import EditActionPointDialog from '@/components/dialogs/EditActionPointDialog.vue';
import ImportActionPointsDialog from '@/components/dialogs/ImportActionPointsDialog.vue';
import ActionPointFilter from '@/components/filters/ActionPointFilter.vue';
import Link from '@/components/Link.vue';
import AssigneeListItem from '@/components/list-items/AssigneeListItem.vue';
import List from '@/components/List.vue';
import { Feature } from '@/enums/feature';
import { OrganizationRole } from '@/enums/roles';
import { ActionListType } from '@/router/actionPoints';
import { useActionPointStore } from '@/stores/actionPoint';
import { useFilterStore } from '@/stores/components/filter';
import { useTourStore } from '@/stores/components/tour';
import { useGlobalStore } from '@/stores/global';
import { useModuleStore } from '@/stores/module';
import { useOrganizationStore } from '@/stores/organization';
import { useSourceStore } from '@/stores/source';
import { useTeamStore } from '@/stores/team';
import { getActionPointStatusItem } from '@/utils/actionPoint';
import { dateSort, isDateBefore, toDisplayDate } from '@/utils/date';
import { fullNameIdPairsToNameIdPairs } from '@/utils/employee';
import { toNameIdPairs } from '@/utils/nameIdPair';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import type { DataTableHeader } from 'vuetify';
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.d.ts';

const actionPointStore = useActionPointStore();
const organizationStore = useOrganizationStore();
const teamStore = useTeamStore();
const moduleStore = useModuleStore();
const sourceStore = useSourceStore();
const authStore = useAuthStore();
const priorityStore = usePriorityStore();
const filterStore = useFilterStore();
const globalStore = useGlobalStore();
const tourStore = useTourStore();

const route = useRoute();
const router = useRouter();

const isInitializing = ref(true);
const searchInput = ref('');

const isCreateActionPointDialogOpen = ref(false);
const isImportActionPointsDialogOpen = ref(false);
const isActionPointEditDialogOpen = ref(false);
const isActionPointDetailsDialogOpen = ref(false);

const selectedActionPoint = ref<DetailedActionPoint | null>(null);
const bulkEditActionPointIds = ref<string[]>();
const isEditDetails = ref(false);

const organization = ref<Organization | null>();
const teams = ref<Team[]>([]);
const employees = ref<Employee[]>([]);
const modules = ref<Module[]>([]);
const risks = ref<Risk[]>([]);
const priorities = ref<Priority[]>([]);
const organizations = ref<NameIdPair[]>([]);

const sortBy = ref<SortItem[]>([{ key: 'deadline', order: 'asc' }]);

const userIsEmployee = computed(() => authStore.isAuthorized([OrganizationRole.Employee]));
const userIsActionCoordinator = computed(() => authStore.organizationEmployeeHasFeature(Feature.ActionCoordinatorRole, organization.value));
const userIsAdmin = computed(() => !userIsActionCoordinator.value && authStore.isAuthorized([OrganizationRole.Admin], organization.value));

const { isActionPointExecutorFeatureEnabled } = storeToRefs(authStore);

const headers = computed(() => {
    let headers: DataTableHeader[] = [
        { title: 'Nr.', value: 'number', sortable: true, width: 50, maxWidth: 50 },
        { title: 'Risiconiv.', key: 'risk', value: (item) => item.risk?.level ?? '-', sortable: true },
        { title: 'Prioriteit', key: 'priority', value: (item) => item.priority?.level ?? '-', sortable: true },
        { title: 'Actie', value: 'action', sortable: true, width: '400', maxWidth: '400' },
        {
            title: 'Verantwoordelijk',
            key: 'responsible',
            value: (item) => item.responsible?.name ?? '',
            sortable: true
        }
    ];

    if (isActionPointExecutorFeatureEnabled.value) {
        headers.push({
            title: 'Uitvoerder',
            key: 'executor',
            value: (item) => item.executor?.name ?? '',
            sortable: true
        });
    }

    headers.push(
        {
            title: 'Deadline',
            value: 'deadline',
            sortable: true,
            sort: (a: Date, b: Date) => dateSort(a, b, sortBy.value.find((i) => i.key == 'deadline')?.order)
        },
        { title: 'Bron', key: 'source', value: (item) => item.source?.name ?? '-', sortable: true }
    );

    if (!moduleStore.areMultipleModulesAccessible) {
        headers = headers.filter((h) => h.title !== 'Module');
    }

    if (authStore.isAuthorized([OrganizationRole.Admin])) {
        headers.push({ title: 'Vestiging', key: 'organization', value: (item) => item.organization?.name ?? '-', sortable: true, width: 150 });
    }

    headers.push({ title: 'Status', key: 'status', value: (item) => getActionPointStatusItem(item.status), sortable: true, width: 125 });

    return headers;
});

onMounted(async () => {
    try {
        organization.value = await getOrganization();
        if (!organization.value) return;

        teams.value = await teamStore.fetch();
        await sourceStore.getGlobalSources();
        await sourceStore.getByOrganizationId(organization.value._id);

        priorities.value = await priorityStore.get();
        risks.value = await actionPointStore.getRisks();

        employees.value = await organizationStore.getAssignableOrganizationEmployees(organization.value._id);

        organizations.value.push({
            _id: organization.value._id,
            name: organization.value.name
        });

        await fetchActionPoints();

        if (route.params.type === ActionListType.ActionPoint && route.params.id) {
            await showActionPoint(route.params.id as string);
        }

        if (tourStore.continueTour === true) tourStore.start();
    } finally {
        isInitializing.value = false;
    }
});

// Needed to show the action point details dialog when the page is loaded with a specific action point id. (e.g. from the notification panel)
watch(
    () => route.params.id,
    async (newId, oldId) => {
        if (!route.params.type) return; // do not re-fetch when showing full list

        // Skip if dialog is already open with this action point (prevents flicker)
        if (isActionPointDetailsDialogOpen.value && selectedActionPoint.value?._id === newId) {
            return;
        }

        if (route.params.type === ActionListType.ActionPoint && newId && newId !== oldId) {
            await showActionPoint(newId.toString());
            return;
        }

        organization.value = await getOrganization();
        await fetchActionPoints();
    }
);

watch(isActionPointDetailsDialogOpen, (newValue) => {
    if (newValue) return;
    router.replace({ path: '/actielijst' });
});

async function fetchActionPoints() {
    if (!organization.value || !authStore.currentOrganization) return;

    const result = await actionPointStore.getDetailsFromOrganization(organization.value?._id, filterStore.selectedFilters);

    // Redirect to own actionlist if no access to given organization id
    if (result.status === 403 && organization.value._id !== authStore.getCurrentOrganization()._id) {
        await router.replace({ name: 'actionPoints' });
        return;
    }

    if (organization.value._id !== authStore.getCurrentOrganization()._id) {
        globalStore.pageTitle = `Actielijst ${organization.value.name}`;
    }
}

async function showActionPoint(actionPointId: string): Promise<void> {
    selectedActionPoint.value = null;

    const actionPoint = await actionPointStore.getDetailsById(actionPointId);
    if (!actionPoint) {
        toast.error('ActionPointNotFound');
        return;
    }

    openActionPointDetailsDialog(actionPoint, false);
}

async function getOrganization() {
    const organizationId = route.params.type === 'org' && route.params.id ? (route.params.id as string) : filterStore.selectedFilters.organization;
    if (organizationId) return await organizationStore.fetchById(organizationId);

    return authStore.getCurrentOrganization();
}

async function changeActionPointStatus(actionPointId: string, status: ActionPointStatus) {
    await actionPointStore.setStatus(actionPointId, status);
}

async function deleteActionPoint(actionPoint: DetailedActionPoint) {
    await actionPointStore.deleteById(actionPoint._id);
}

async function deleteActionPoints(ids: string[]) {
    await actionPointStore.deleteByIds(ids);
}

function openActionPointDetailsDialog(actionPoint: DetailedActionPoint | null, isEdit: boolean, bulkEditIds?: string[]) {
    selectedActionPoint.value = actionPoint;
    bulkEditActionPointIds.value = bulkEditIds;

    if (isEdit) {
        isActionPointEditDialogOpen.value = true;
    } else {
        isActionPointDetailsDialogOpen.value = true;
        // Update route when opening from list (not from URL navigation)
        if (actionPoint && route.params.id !== actionPoint._id) {
            router.replace({ path: `/actielijst/actie/${actionPoint._id}` });
        }
    }
}

function closeActionPointDetailsDialog() {
    isActionPointDetailsDialogOpen.value = false;
    isEditDetails.value = false;
    selectedActionPoint.value = null;
}

function updateActionPointInList(updatedActionPoint: DetailedActionPoint) {
    const index: number = actionPointStore.actionPoints.findIndex((acp) => acp._id === updatedActionPoint._id);

    if (index === -1) {
        actionPointStore.actionPoints.push(updatedActionPoint);
        return;
    }

    actionPointStore.actionPoints[index] = updatedActionPoint;
}

function addListItem(newActionPoint: DetailedActionPoint) {
    actionPointStore.actionPoints.push(newActionPoint);
}

function isEditItemActionEnabled(actionPoint: DetailedActionPoint): boolean {
    return userIsAdmin.value || userIsActionCoordinator.value || actionPoint.createdBy?._id === authStore.loggedInEmployee?._id;
}

function isDeleteItemActionEnabled(actionPoint: DetailedActionPoint): boolean {
    if (userIsActionCoordinator.value) return false;
    return isEditItemActionEnabled(actionPoint);
}

function isActionPointPastDeadline(actionPoint: DetailedActionPoint): boolean {
    return actionPoint.deadline != null && isDateBefore(actionPoint.deadline);
}
</script>

<template>
    <div>
        <v-fade-transition appear>
            <v-row class="justify-space-between align-center mb-3">
                <v-col lg="4" md="5" sm="12" cols="12" class="d-flex ga-3 pa-0">
                    <ActionPointFilter
                        v-if="organization"
                        @apply-filters="fetchActionPoints"
                        :teams
                        :employees
                        :priorities
                        :sources="sourceStore.allSources"
                        :risks
                        :organization
                        :isInitializing
                    />
                    <v-text-field v-model="searchInput" hide-details label="Zoeken" placeholder="Typ om te zoeken..." />
                </v-col>
                <v-col lg="8" md="7" sm="12" class="d-flex ga-5 justify-md-end mt-5 pa-0" v-if="!authStore.isReadOnly()">
                    <CustomButton
                        v-if="userIsAdmin"
                        id="importActionPointsButton"
                        color="primary"
                        @click="isImportActionPointsDialogOpen = true"
                        text="Actiepunten importeren"
                    />
                    <CustomButton v-if="userIsEmployee" @click="isCreateActionPointDialogOpen = true" text="Nieuw actiepunt" />
                </v-col>
            </v-row>
        </v-fade-transition>
        <v-fade-transition>
            <List
                v-show="(!isInitializing && !globalStore.isLoading) || isActionPointDetailsDialogOpen"
                :is-initializing="isInitializing"
                :search="searchInput"
                :show-select="authStore.isAuthorized([OrganizationRole.Admin])"
                :items-per-page="15"
                fixed-header
                :show-search="false"
                no-data-text="Geen gegevens"
                :sort-by="sortBy"
                :headers
                :items="actionPointStore.actionPoints"
                :is-editable="true"
                :is-deletable="true"
                entity-name="Actiepunt"
                name-property="action"
                :editEnabled="isEditItemActionEnabled"
                :delete-enabled="isDeleteItemActionEnabled"
                :bulk-delete-enabled="() => !userIsActionCoordinator"
                @click-table-row="(item) => openActionPointDetailsDialog(item, false)"
                @click-table-row-edit="(item) => openActionPointDetailsDialog(item, true)"
                @delete-confirmed="deleteActionPoint"
                @bulk-edit-clicked="(selected) => openActionPointDetailsDialog(null, true, selected)"
                @bulk-delete-confirmed="deleteActionPoints"
            >
                <template #item.number="{ item }">
                    <span class="item-number">{{ item.number }}</span>
                </template>
                <template #item.module="{ item }">
                    <Link :route-to="{ name: 'module', params: { module: item.module?.name } }" icon="grid_view" :displayText="item.module.name" />
                </template>

                <template #item.risk="{ item }">
                    <DotText :value="item.risk?.description || ''" :color="item.risk?.color || 'red'" />
                </template>

                <template #item.priority="{ item }">
                    <DotText :value="item.priority?.name || ''" :color="item.priority?.color || 'red'" />
                </template>

                <template #item.action="{ item }">
                    <p class="text-truncate ma-0 text-md">{{ item.action }}</p>
                </template>

                <template #item.source="{ item }">
                    <DotText :value="item.source?.name" :color="item.source?.color || 'none'" />
                </template>

                <template #item.responsible="{ item }">
                    <AssigneeListItem v-if="item.responsible" :detailedAssignee="item.responsible" />
                    <span v-else>-</span>
                </template>

                <template #item.executor="{ item }" v-if="isActionPointExecutorFeatureEnabled">
                    <AssigneeListItem v-if="item.executor" :detailedAssignee="item.executor" />
                    <span v-else>-</span>
                </template>

                <template #item.deadline="{ item }">
                    <div class="d-flex align-center">
                        <v-tooltip
                            :disabled="!isActionPointPastDeadline(item)"
                            text="Actiepunt is verlopen!"
                            #activator="{ props }"
                            location="bottom"
                        >
                            <span v-bind="props" :class="['text-no-wrap', { 'text-red font-weight-bold': isActionPointPastDeadline(item) }]">{{
                                toDisplayDate(item.deadline)
                            }}</span>
                        </v-tooltip>
                    </div>
                </template>

                <template #item.organization="{ item }">
                    <Link
                        icon="business"
                        :displayText="item.organization.name"
                        :routeTo="`/bedrijf/${item.organization._id}/informatie`"
                        spanClass="truncate"
                    />
                </template>

                <template #item.status="{ item, value: currentStatus }">
                    <v-tooltip v-if="!authStore.isReadOnly()" location="bottom" text="Status wijzigen" #activator="{ props }">
                        <div class="pa-0 d-flex" v-bind="props" id="actionPointStatusMenu">
                            <v-menu location="bottom" class="ma-0">
                                <template #activator="{ props }">
                                    <Chip v-bind="props" :color="currentStatus.color" :text="currentStatus.title" />
                                </template>
                                <v-list class="pa-0">
                                    <v-list-item
                                        v-for="(status, index) in actionPointStatusTypeItems.filter((i) => i.value !== currentStatus.value)"
                                        class="hover-main"
                                        :key="index"
                                        @click="changeActionPointStatus(item._id, status.value)"
                                    >
                                        {{ status.title }}
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </v-tooltip>
                    <Chip v-else :color="currentStatus.color" :text="currentStatus.title" />
                </template>
            </List>
        </v-fade-transition>

        <CreateActionPointDialog
            v-if="!isInitializing"
            @close-dialog="isCreateActionPointDialogOpen = false"
            @add-list-item="addListItem"
            v-model="isCreateActionPointDialogOpen"
            :modules="toNameIdPairs(modules)"
            :employees="fullNameIdPairsToNameIdPairs(employees)"
            :teams="toNameIdPairs(teams)"
            :risks="risks"
            :sources="sourceStore.allSources"
            :priorities="priorities"
            :organization="organization"
            :userIsActionCoordinator="userIsActionCoordinator"
        />

        <ImportActionPointsDialog
            v-if="organization"
            :organization-id="organization?._id"
            v-model="isImportActionPointsDialogOpen"
            @close-dialog="isCreateActionPointDialogOpen = false"
            @done="fetchActionPoints"
        />

        <!-- TODO: Create seperate endpoints instead of these maps so we do not fetch the whole objects but just the id/name pairs -->
        <EditActionPointDialog
            v-if="selectedActionPoint || (bulkEditActionPointIds && bulkEditActionPointIds.length > 0)"
            v-model="isActionPointEditDialogOpen"
            :selectedActionPoint
            :sourceNameIdPairs="sourceStore.allSources"
            :teamNameIdPairs="toNameIdPairs(teams)"
            :employeeNameIdPairs="fullNameIdPairsToNameIdPairs(employees)"
            :moduleNameIdPairs="toNameIdPairs(modules)"
            :riskNameIdPairs="risks.map((r) => ({ _id: r._id, description: r.description }))"
            :bulk-edit-ids="bulkEditActionPointIds"
            @updateActionPoint="updateActionPointInList"
            @bulk-updated="fetchActionPoints"
            :userIsActionCoordinator="userIsActionCoordinator"
        />

        <ActionPointDetailsDialog
            :actionPoint="selectedActionPoint"
            v-model="isActionPointDetailsDialogOpen"
            @close-dialog="closeActionPointDetailsDialog()"
        />
    </div>
</template>
